// EventsLastPeriodPage — events recorded in a configurable look-back period.
// Shows summary cards (total, by type) and a filterable table of events.

import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { useApi } from '@/lib/api/client'
import { useTenant } from '@/components/shared/TenantShell'
import { reportingApi } from '@/lib/api/reporting'
import type { EventReportRow } from '@/lib/api/reporting'
import { queryKeys } from '@/lib/api/queryKeys'
import { ErrorState } from '@/components/shared/ErrorState'
import { TableSkeleton } from '@/components/shared/LoadingSkeleton'
import { Zap, Calendar } from 'lucide-react'

const PERIOD_OPTIONS = [7, 14, 30, 60, 90] as const

export function EventsLastPeriodPage() {
  const { slug } = useTenant()
  const api = useApi()
  const rptApi = reportingApi(api)
  const navigate = useNavigate()
  const [days, setDays] = useState<number>(30)

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: queryKeys.reporting.eventsLastPeriod(slug, { days }),
    queryFn: () => rptApi.eventsLastPeriod(days),
  })

  if (isLoading) return <TableSkeleton rows={8} />

  if (error) {
    return (
      <div className="p-6">
        <ErrorState
          message="Failed to load events report"
          technicalDetail={error.message}
          onRetry={() => refetch()}
        />
      </div>
    )
  }

  if (!data) return null

  return (
    <div className="space-y-4 overflow-y-auto p-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold">Recent Events</h1>
          <p className="text-sm text-muted-foreground">
            Events recorded in the last {data.period_days} days, filtered by your permissions.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <Calendar className="h-4 w-4 text-muted-foreground" />
          <label htmlFor="period-days" className="text-sm text-muted-foreground">
            Period:
          </label>
          <select
            id="period-days"
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
            className="rounded-md border bg-background px-2 py-1 text-sm"
          >
            {PERIOD_OPTIONS.map((n) => (
              <option key={n} value={n}>
                Last {n} days
              </option>
            ))}
          </select>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <SummaryCard label="Total Events" value={data.total_count} />
        {data.by_type.map((t) => (
          <SummaryCard key={t.event_type_id} label={t.event_type_name} value={t.count} />
        ))}
      </div>

      {/* Events table */}
      {data.events.length === 0 ? (
        <div className="flex h-48 items-center justify-center text-muted-foreground">
          <div className="space-y-2 text-center">
            <Zap className="mx-auto h-10 w-10 text-muted-foreground/40" />
            <p className="text-sm font-medium">No events in this period</p>
            <p className="text-xs">
              No events were recorded in the last {data.period_days} days that you have permission
              to view.
            </p>
          </div>
        </div>
      ) : (
        <div className="overflow-hidden rounded-md border">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b bg-muted/50">
                <th className="px-3 py-2 text-left font-medium">Event</th>
                <th className="px-3 py-2 text-left font-medium">Type</th>
                <th className="px-3 py-2 text-left font-medium">Category</th>
                <th className="px-3 py-2 text-left font-medium">Date</th>
                <th className="px-3 py-2 text-left font-medium">State</th>
                <th className="px-3 py-2 text-left font-medium">Recorded By</th>
                <th className="px-3 py-2 text-center font-medium">Risks</th>
                <th className="px-3 py-2 text-center font-medium">Actions</th>
              </tr>
            </thead>
            <tbody>
              {data.events.map((ev) => (
                <EventRow
                  key={ev.id}
                  event={ev}
                  onClick={() => navigate({ to: `/t/${slug}/events/${ev.id}` })}
                />
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  )
}

// --- Sub-components ---

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-md border p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className="text-2xl font-bold tabular-nums">{value}</p>
    </div>
  )
}

function EventRow({ event, onClick }: { event: EventReportRow; onClick: () => void }) {
  return (
    <tr className="cursor-pointer border-b last:border-0 hover:bg-muted/30" onClick={onClick}>
      <td className="max-w-[200px] truncate px-3 py-2 font-medium" title={event.title}>
        {event.title}
      </td>
      <td className="px-3 py-2 text-muted-foreground">{event.event_type_name || '—'}</td>
      <td className="px-3 py-2 text-muted-foreground">{event.category_name || '—'}</td>
      <td className="whitespace-nowrap px-3 py-2 tabular-nums">
        {event.event_date ? new Date(event.event_date).toLocaleDateString() : '—'}
      </td>
      <td className="px-3 py-2">
        <StateBadge state={event.lifecycle_state} />
      </td>
      <td className="max-w-[120px] truncate px-3 py-2 text-muted-foreground">
        {event.recorded_by_name || '—'}
      </td>
      <td className="px-3 py-2 text-center tabular-nums">{event.linked_risk_count}</td>
      <td className="px-3 py-2 text-center tabular-nums">{event.linked_action_count}</td>
    </tr>
  )
}

function StateBadge({ state }: { state: string }) {
  const styles: Record<string, string> = {
    draft: 'bg-gray-100 text-gray-700',
    active: 'bg-green-100 text-green-700',
    closed: 'bg-blue-100 text-blue-700',
  }
  return (
    <span
      className={`inline-block rounded-full px-2 py-0.5 text-xs font-medium ${styles[state] ?? 'bg-gray-100 text-gray-700'}`}
    >
      {state}
    </span>
  )
}
